import { useEffect, useMemo, useState } from "react";
import { formatUzPhone } from "../phone";
import {
  api,
  fmtDateTime,
  TICKET_STATUS_LABEL,
  TICKET_TYPE_LABEL,
  type SupportTicket,
  type TicketStatus,
  type TicketType,
} from "../api";
import { StatusBadge, TariffBadge, TicketStatusBadge } from "../components/Badge";
import { Modal } from "../components/Modal";

export function Tickets() {
  const [rows, setRows] = useState<SupportTicket[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<"" | TicketStatus>("");
  const [filterType, setFilterType] = useState<"" | TicketType>("");
  const [selected, setSelected] = useState<SupportTicket | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await api.supportTickets();
        setRows(t);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let out = rows;
    if (filterStatus) out = out.filter((t) => t.status === filterStatus);
    if (filterType) out = out.filter((t) => t.type === filterType);
    return out;
  }, [rows, filterStatus, filterType]);

  function replaceRow(updated: SupportTicket) {
    setRows((cur) => cur ? cur.map((t) => (t.id === updated.id ? updated : t)) : cur);
  }

  function openTicket(t: SupportTicket) {
    setSelected(t);
    if (t.status === "new") {
      // Ochilganda avtomatik "ko'rildi" — ro'yxat qayta yuklanmaydi.
      api.markTicketSeen(t.id)
        .then((updated) => {
          replaceRow(updated);
          setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
        })
        .catch(() => { /* ko'rsatishga xalal bermaydi */ });
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Murojaatlar</h1>
          <p className="page-sub">Ustaxonalardan kelgan aloqa va taklif/shikoyatlar</p>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="filters">
        <label>
          Status
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as "" | TicketStatus)}>
            <option value="">Hammasi</option>
            <option value="new">Yangi</option>
            <option value="seen">Ko'rildi</option>
            <option value="replied">Javob berildi</option>
          </select>
        </label>
        <label>
          Turi
          <select value={filterType} onChange={(e) => setFilterType(e.target.value as "" | TicketType)}>
            <option value="">Hammasi</option>
            <option value="support">Aloqa</option>
            <option value="feedback">Taklif/Shikoyat</option>
          </select>
        </label>
      </div>

      <div className="panel flush">
        {!rows ? (
          <div className="empty">Yuklanmoqda…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">{rows.length === 0 ? "Hali murojaatlar yo'q" : "Mos murojaat topilmadi"}</div>
        ) : (
          <table className="table">
            <thead><tr>
              <th>Kimdan</th>
              <th>Ustaxona</th>
              <th>Turi</th>
              <th>Mazmun</th>
              <th>Status</th>
              <th>Sana</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  className={"clickable" + (t.status === "new" ? " row-new" : "")}
                  onClick={() => openTicket(t)}
                >
                  <td>
                    <strong>{t.sender.full_name}</strong>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {t.sender.role === "workshop_owner" ? "Egasi" : "Xodim"}
                    </div>
                  </td>
                  <td>{t.workshop.name}</td>
                  <td>{TICKET_TYPE_LABEL[t.type]}</td>
                  <td className="ticket-title-cell">{t.title}</td>
                  <td><TicketStatusBadge status={t.status} /></td>
                  <td>{fmtDateTime(t.created_at)}</td>
                  <td className="right">
                    <button className="icon-btn" onClick={(e) => { e.stopPropagation(); openTicket(t); }}>
                      Ko'rish →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <DetailModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => { replaceRow(updated); setSelected(updated); }}
        />
      )}
    </>
  );
}

function DetailModal({
  ticket,
  onClose,
  onUpdated,
}: {
  ticket: SupportTicket;
  onClose: () => void;
  onUpdated: (t: SupportTicket) => void;
}) {
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sendWarn, setSendWarn] = useState(false);

  const noTelegram = ticket.sender.telegram_id === null;

  async function sendReply(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await api.replyTicket(ticket.id, replyText.trim());
      if (!r.telegram_sent && !noTelegram) setSendWarn(true);
      const { telegram_sent: _ts, ...updated } = r;
      onUpdated(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <Modal onClose={onClose} wide labelledBy="ticket-modal-title">
      <div className="modal-head">
        <h2 id="ticket-modal-title">{TICKET_TYPE_LABEL[ticket.type]} — #{ticket.id}</h2>
        <div className="sub">{fmtDateTime(ticket.created_at)} · {TICKET_STATUS_LABEL[ticket.status]}</div>
      </div>
        <div className="modal-body">
          {err && <div className="error">{err}</div>}

          <div className="ticket-section">
            <div className="ticket-section-title">Yuboruvchi</div>
            <div className="kv">
              <div className="k">Ism</div><div>{ticket.sender.full_name}</div>
              <div className="k">Telefon</div><div>{formatUzPhone(ticket.sender.phone) || "—"}</div>
              <div className="k">Rol</div><div>{ticket.sender.role === "workshop_owner" ? "Egasi" : "Xodim"}</div>
              <div className="k">Telegram</div>
              <div>{ticket.sender.telegram_id ?? "—"}</div>
            </div>
            {noTelegram && (
              <div className="warn-note">Telegram ulanmagan — telefon orqali bog'laning</div>
            )}
          </div>

          <div className="ticket-section">
            <div className="ticket-section-title">Ustaxona</div>
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <strong>{ticket.workshop.name}</strong>
              <TariffBadge tariff={ticket.workshop.tariff} />
              <StatusBadge ws={ticket.workshop} />
            </div>
          </div>

          <div className="ticket-section">
            <div className="ticket-section-title">Murojaat</div>
            <h3 className="ticket-title">{ticket.title}</h3>
            <div className="ticket-body">{ticket.body}</div>
          </div>

          <div className="ticket-section">
            <div className="ticket-section-title">Javob</div>
            {ticket.status === "replied" ? (
              <>
                <div className="ticket-body">{ticket.admin_reply}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Yuborilgan: {fmtDateTime(ticket.replied_at)}
                </div>
                {sendWarn && (
                  <div className="warn-note">Telegram'ga yuborib bo'lmadi — javob saqlandi, telefon orqali bog'laning</div>
                )}
              </>
            ) : (
              <form className="form" onSubmit={sendReply}>
                <div className="field">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                    placeholder="Javob matni…"
                  />
                </div>
                <div className="row" style={{ justifyContent: "flex-end" }}>
                  <button type="submit" className="btn" disabled={busy || !replyText.trim()}>
                    {busy ? "..." : noTelegram ? "Javobni saqlash" : "Telegram orqali javob berish"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      <div className="actions">
        <button type="button" className="btn ghost" onClick={onClose}>Yopish</button>
      </div>
    </Modal>
  );
}
